import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Fade from '@mui/material/Fade';
import { registerConfirmHandler, subscribeNotify } from './notify';

const MAX_TOASTS = 4;

function Toast({ toast, onClose }) {
    useEffect(() => {
        const timer = setTimeout(() => onClose(toast.id), toast.duration);
        return () => clearTimeout(timer);
    }, [toast, onClose]);

    return (
        <Fade in appear>
            <Alert
                severity={toast.severity}
                variant="outlined"
                onClose={() => onClose(toast.id)}
                sx={{
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)',
                    width: { xs: '100%', sm: 380 },
                    color: 'text.primary',
                }}
            >
                {toast.title && <AlertTitle sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{toast.title}</AlertTitle>}
                {toast.message}
            </Alert>
        </Fade>
    );
}

export default function FeedbackProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState(null);
    const resolverRef = useRef(null);

    useEffect(
        () =>
            subscribeNotify((toast) => {
                setToasts((prev) => [...prev, toast].slice(-MAX_TOASTS));
            }),
        []
    );

    useEffect(
        () =>
            registerConfirmHandler(
                (options) =>
                    new Promise((resolve) => {
                        resolverRef.current = resolve;
                        setConfirmState(options || {});
                    })
            ),
        []
    );

    const closeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const resolveConfirm = (value) => {
        resolverRef.current?.(value);
        resolverRef.current = null;
        setConfirmState(null);
    };

    return (
        <>
            {children}
            <Box
                aria-live="polite"
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    left: { xs: 16, sm: 'auto' },
                    zIndex: (t) => t.zIndex.snackbar,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'flex-end',
                    pointerEvents: 'none',
                    '& > *': { pointerEvents: 'auto' },
                }}
            >
                {toasts.map((toast) => (
                    <Toast key={toast.id} toast={toast} onClose={closeToast} />
                ))}
            </Box>
            <Dialog open={Boolean(confirmState)} onClose={() => resolveConfirm(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{confirmState?.title || 'Confirmar ação'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmState?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => resolveConfirm(false)}>
                        {confirmState?.cancelText || 'Cancelar'}
                    </Button>
                    <Button
                        variant="contained"
                        color={confirmState?.destructive ? 'error' : 'primary'}
                        onClick={() => resolveConfirm(true)}
                        autoFocus
                    >
                        {confirmState?.confirmText || 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
